import React, { useState, useEffect, useLayoutEffect, useRef, Fragment } from 'react';
import PropTypes from 'prop-types';
import parse from 'html-react-parser';
import { FormattedMessage } from 'react-intl';

import { stripTags, truncate as truncateUtil } from '../../util/text.js';

// useLayoutEffect warns during SSR (no DOM to measure). Fall back to useEffect
// on the server, where the effect never runs anyway.
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

function HtmlComponent({ text = '', inline = false, useCollapse = false, truncate = 0, collapsedSize = 0 }) {
    const useParser = true; // Faster with parser

    const collapsedRem = collapsedSize || 10;
    const collapsible = !inline && useCollapse;

    const contentRef = useRef(null);
    const [isCollapsed, setCollapse] = useState(collapsible);
    // Whether the full text actually exceeds the collapsed zone height.
    const [isOverflowing, setIsOverflowing] = useState(false);

    // Measure the natural content height against the collapsed zone. The full
    // text is always rendered in the DOM (readable by search engines); only the
    // visual clipping and the "Read more" link depend on real overflow.
    useIsomorphicLayoutEffect(() => {
        if(!collapsible) {
            return undefined;
        }

        const measure = () => {
            const el = contentRef.current;
            if(!el) {
                return;
            }
            const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
            const maxHeightPx = collapsedRem * rootFontSize;
            // scrollHeight reports full content height even while visually clipped.
            setIsOverflowing(el.scrollHeight > maxHeightPx + 1);
        };

        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [collapsible, collapsedRem, text]);

    const handleOpen = () => {
        setCollapse(false);
    };

    const html = truncate ? stripTags(truncateUtil(text, truncate)) : text;

    const renderHtml = () => {
        if(html) {
            let transformedText;
            if(useParser) {
                try {
                    transformedText = parse(html);
                } catch (e) {
                    transformedText = <span dangerouslySetInnerHTML={{ __html: html }} />;
                }
            } else {
                transformedText = html;
            }

            return (
                <Fragment>
                    {transformedText}
                    {truncate ? '...' : ''}
                </Fragment>
            );
        }
    };

    const Tag = inline ? 'span' : 'div';
    const tagProps = {
		className: 'html-text-wrapper',
    };

    if(!useParser) {
        tagProps.dangerouslySetInnerHTML = {
            __html: html,
        };
    }

	const renderContent = () => (useParser ? <Tag {...tagProps}>{renderHtml()}</Tag> : <Tag {...tagProps} />);

	if(collapsible) {
		const clip = isCollapsed && isOverflowing;
		return (
			<div className="html-text-wrapper">
				<div ref={contentRef} className="overflow-hidden" style={{ maxHeight: clip ? `${collapsedRem}rem` : 'none' }}>
					{renderContent()}
				</div>
				{clip && <Tag className="mt-1 text-end text-lg text-decoration-underline see-more" onClick={handleOpen}> <span><FormattedMessage id="readMore" defaultMessage="Read more" />{' >'}</span></Tag>}
			</div>
		);
	}
	return renderContent();
}

HtmlComponent.propTypes = {
    text: PropTypes.string,
    inline: PropTypes.bool,
    useCollapse: PropTypes.bool,
    truncate: PropTypes.number,
    collapsedSize: PropTypes.number,
};

export default HtmlComponent;
